import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X } from "lucide-react";
import { useLang, scrollToId } from "../../site-context";
import { LINKS } from "../../i18n";

const LangSwitch = () => {
  const { lang, setLang } = useLang();
  return (
    <div className="flex items-center rounded-full border border-white/10 bg-white/5 p-1">
      {["pt", "en"].map((l) => (
        <button
          key={l}
          data-testid={`language-switcher-${l}`}
          onClick={() => setLang(l)}
          className={`ff-mono rounded-full px-3 py-1 text-[11px] uppercase tracking-widest transition-colors duration-300 ${
            lang === l ? "bg-[#e3a008] text-[#0a1120]" : "text-slate-400 hover:text-white"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

export default function Header() {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "servicos", label: t.nav.services, testid: "nav-link-services" },
    { id: "frota", label: t.nav.fleet, testid: "nav-link-fleet" },
    { id: "historia", label: t.nav.heritage, testid: "nav-link-heritage" },
    { id: "contactos", label: t.nav.contact, testid: "nav-link-contact" },
  ];

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled
          ? "border-b border-white/5 bg-[#0a1120]/85 backdrop-blur-xl"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
        <button
          data-testid="header-logo"
          onClick={() => go("inicio")}
          className="flex items-center gap-3"
        >
          <img
            src="/assets/lion-mark.png"
            alt="Táxis Leão & Henrique"
            className="h-10 w-10 rounded-full ring-1 ring-[#e3a008]/40"
          />
          <div className="text-left leading-tight">
            <span className="ff-display block text-lg font-semibold text-white">
              Leão <span className="text-[#e3a008]">&</span> Henrique
            </span>
            <span className="ff-mono block text-[10px] uppercase tracking-[0.3em] text-[#e3a008]/80">
              Paços de Ferreira
            </span>
          </div>
        </button>

        <nav className="hidden items-center gap-8 lg:flex">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={l.testid}
              onClick={() => go(l.id)}
              className="group relative text-sm font-medium text-slate-300 transition-colors duration-300 hover:text-white"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-[#e3a008] transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <div className="hidden items-center gap-4 lg:flex">
          <LangSwitch />
          <motion.a
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.97 }}
            href={LINKS.phone}
            data-testid="header-call-button"
            className="flex items-center gap-2 rounded-full bg-[#e3a008] px-5 py-2.5 text-sm font-semibold text-[#0a1120] shadow-[0_0_24px_rgba(227,160,8,0.35)]"
          >
            <Phone className="h-4 w-4" />
            {LINKS.phoneDisplay}
          </motion.a>
        </div>

        <button
          data-testid="mobile-menu-button"
          onClick={() => setOpen(!open)}
          className="rounded-lg border border-white/10 p-2 text-white lg:hidden"
          aria-label="Menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-b border-white/5 bg-[#0a1120]/95 backdrop-blur-xl lg:hidden"
          >
            <div className="flex flex-col gap-1 px-4 py-4">
              {links.map((l) => (
                <button
                  key={l.id}
                  data-testid={`mobile-${l.testid}`}
                  onClick={() => go(l.id)}
                  className="rounded-lg px-4 py-3 text-left text-base text-slate-200 transition-colors hover:bg-white/5"
                >
                  {l.label}
                </button>
              ))}
              <div className="mt-3 flex items-center justify-between px-4 pb-2">
                <LangSwitch />
                <a
                  href={LINKS.phone}
                  data-testid="mobile-header-call-button"
                  className="flex items-center gap-2 rounded-full bg-[#e3a008] px-4 py-2 text-sm font-semibold text-[#0a1120]"
                >
                  <Phone className="h-4 w-4" />
                  {LINKS.phoneDisplay}
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
